// modules/freshcards/src/ThemeEngine.ts
//
// 2026-09-03 (Phase 12) — ThemeEngine.
//
// Applies a Theme to the document root as CSS variables. Components
// already consume those vars (`var(--accent)`, `var(--bg)`, etc.),
// so the engine is a pure DOM write — no React, no ctx.
//
// The engine also handles export/import of a theme.json bundle that
// external apps can ship. The format is documented and stable.

import { loadAccountTheme, saveAccountTheme, type Theme } from './ThemeAPI';
import { loadLayout, type Layout } from './LayoutAPI';
import { layoutToCssVars } from './LayoutAPI';

const STYLE_ID = 'freshcards-theme-vars';
const STYLE_ID_LAYOUT = 'freshcards-layout-vars';

const PALETTE_TO_VAR: Record<string, string> = {
  accent: '--accent',
  bg: '--bg',
  sub: '--sub',
  text: '--text',
  textMuted: '--text-muted',
  border: '--border-inner',
  accentBg: '--accent-bg',
  aiAccent: '--ai-accent',
};

const SHADOW_BY_STRENGTH: Record<NonNullable<Theme['geometry']['shadow']>, string> = {
  none: 'none',
  sm: '0 1px 2px rgba(0,0,0,0.18)',
  md: '0 4px 12px rgba(0,0,0,0.20)',
  lg: '0 8px 24px rgba(0,0,0,0.24)',
};

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

function ensureStyle(id: string, doc: Document = typeof document !== 'undefined' ? document : (null as any)): HTMLStyleElement | null {
  if (!doc) return null;
  let el = doc.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = doc.createElement('style');
    el.id = id;
    doc.head.appendChild(el);
  }
  return el;
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const el = ensureStyle(STYLE_ID);
  if (!el) return;
  const lines: string[] = [':root {'];
  for (const [k, cssVar] of Object.entries(PALETTE_TO_VAR)) {
    const v = (theme.palette as Record<string, any>)[k];
    if (v) lines.push(`  ${cssVar}: ${v};`);
  }
  // Geometry → radius / shadow / border
  lines.push(`  --fc-card-radius: ${theme.geometry.radius}px;`);
  lines.push(`  --fc-card-padding: ${theme.geometry.padding}px;`);
  lines.push(`  --fc-card-border-width: ${theme.geometry.borderWidth}px;`);
  lines.push(`  --fc-card-shadow: ${SHADOW_BY_STRENGTH[theme.geometry.shadow]};`);
  // Typography
  lines.push(`  --fc-base-font-size: ${theme.typography.baseFontSize}px;`);
  lines.push(`  --fc-font-family: ${theme.typography.fontFamily};`);
  lines.push(`  --fc-heading-font-family: ${theme.typography.headingFontFamily ?? theme.typography.fontFamily};`);
  lines.push('}');
  // Semantic block style
  lines.push(`.fc-semantic-block {`);
  lines.push(`  border-radius: ${theme.geometry.radius}px;`);
  lines.push(`  padding: ${theme.geometry.padding}px;`);
  lines.push(`  border-width: ${theme.geometry.borderWidth}px;`);
  lines.push(`}`);
  el.textContent = lines.join('\n');
}

export function applyLayout(layout: Layout): void {
  if (typeof document === 'undefined') return;
  const el = ensureStyle(STYLE_ID_LAYOUT);
  if (!el) return;
  const vars = layoutToCssVars(layout);
  const lines: string[] = [':root {'];
  for (const [k, v] of Object.entries(vars)) {
    lines.push(`  ${k}: ${v};`);
  }
  lines.push('}');
  el.textContent = lines.join('\n');
}

/** Apply both the active account theme and the active layout. */
export function applyCurrent(): void {
  applyTheme(loadAccountTheme());
  applyLayout(loadLayout());
}

// ---------------------------------------------------------------------------
// Export / Import
// ---------------------------------------------------------------------------

export interface ThemeBundle {
  /** Schema version. Bump when the format changes. */
  schemaVersion: 1;
  /** When the bundle was exported. */
  exportedAt: string;
  /** The theme. */
  theme: Theme;
  /** Optional layout. */
  layout?: Layout;
}

export function exportThemeBundle(opts?: { includeLayout?: boolean }): string {
  const theme = loadAccountTheme();
  const bundle: ThemeBundle = {
    schemaVersion: 1,
    exportedAt: new Date().toISOString(),
    theme,
  };
  if (opts?.includeLayout) bundle.layout = loadLayout();
  return JSON.stringify(bundle, null, 2);
}

export interface ImportResult {
  ok: boolean;
  theme?: Theme;
  layout?: Layout;
  error?: string;
}

export function importThemeBundle(json: string): ImportResult {
  try {
    const parsed = JSON.parse(json) as ThemeBundle;
    if (parsed.schemaVersion !== 1) {
      return { ok: false, error: `Unsupported schema version: ${parsed.schemaVersion}` };
    }
    if (!parsed.theme || !parsed.theme.palette) {
      return { ok: false, error: 'Missing theme in bundle' };
    }
    const theme: Theme = {
      ...parsed.theme,
      createdBy: 'imported',
    };
    saveAccountTheme(theme);
    if (parsed.layout) {
      // Save layout if provided
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('freshcards:layout', JSON.stringify(parsed.layout));
      }
    }
    return { ok: true, theme, layout: parsed.layout };
  } catch (e) {
    return { ok: false, error: String((e as Error).message ?? e) };
  }
}

// ---------------------------------------------------------------------------
// Bundle hash — stable string for cache-busting
// ---------------------------------------------------------------------------

export function bundleHash(): string {
  const theme = loadAccountTheme();
  const layout = loadLayout();
  const blob = JSON.stringify({ theme, layout });
  // djb2 — short, stable, no crypto needed
  let hash = 5381;
  for (let i = 0; i < blob.length; i++) {
    hash = ((hash << 5) + hash) + blob.charCodeAt(i);
    hash = hash | 0;
  }
  return (hash >>> 0).toString(36);
}
